import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Music, ArrowRight, Mail, Lock, User, Loader2, Building2, Globe } from 'lucide-react';
import { toast } from 'sonner';
import Strings from '../components/Strings';
import { useAuth } from '../context/AuthContext';
import { AuthAPI, errText } from '../api';
import { IMG } from '../mock';

const COPY = {
  signin: ['Welcome back.', 'Sign in'],
  signup: ['Create account.', 'Create account'],
  forgot: ['Reset password.', 'Email me a reset link'],
};

export default function Auth() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login, register } = useAuth();
  const isStudio = loc.pathname.includes('studio');
  const [mode, setMode] = useState(loc.pathname === '/signup' ? 'signup' : 'signin');
  const [form, setForm] = useState({ owner: '', name: '', email: '', password: '', website: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === 'forgot') {
        const r = await AuthAPI.forgot(form.email);
        toast.success('Check your inbox.', { description: r.message });
        setMode('signin');
        return;
      }
      const user = mode === 'signin'
        ? await login({ email: form.email, password: form.password })
        : await register({ business_name: form.name, owner_name: form.owner, email: form.email, password: form.password, website: form.website });
      if (mode === 'signup') toast.success('Welcome to Brand Melody.', { description: 'Your first song request is already with Perry.' });
      const next = new URLSearchParams(window.location.search).get('next');
      nav(user.role === 'studio' ? '/studio' : (next && next.startsWith('/') ? next : '/dashboard'));
    } catch (err) {
      toast.error(errText(err, mode === 'signin' ? 'Could not sign you in.' : 'Something went wrong.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 grain">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={IMG.heroPiano} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(120deg,#0A0806 10%,rgba(10,8,6,0.5))' }} />
        <Strings density={7} opacity={0.4} />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-3">
            <span className="grid place-items-center w-11 h-11 rounded-full border hairline"><Music size={18} className="text-gold" /></span>
            <span className="font-display text-xl text-cream">Brand <span className="italic text-gold-grad">Melody</span></span>
          </Link>
          <div>
            <h2 className="font-display text-5xl text-cream leading-[0.95]">Your business,<br /><span className="italic text-gold-grad">set to music.</span></h2>
            <p className="mt-4 text-cream/60 max-w-sm">Sign in to hear your versions, choose the one that sounds like you, and put it to work.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-ink">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-10">
            <span className="grid place-items-center w-10 h-10 rounded-full border hairline"><Music size={16} className="text-gold" /></span>
            <span className="font-display text-lg text-cream">Brand <span className="italic text-gold-grad">Melody</span></span>
          </Link>
          <p className="eyebrow mb-3">{isStudio ? "Perry's studio" : 'Business account'}</p>
          <h1 className="font-display text-4xl text-cream mb-3">{COPY[mode][0]}</h1>
          {mode === 'signup' && <p className="text-sm text-cream/55 mb-6">Create your account and your first song request goes straight to Perry.</p>}
          {mode === 'forgot' && <p className="text-sm text-cream/55 mb-6">Enter your email and we&rsquo;ll send a link to choose a new password.</p>}
          <form onSubmit={submit} className="space-y-4" data-testid={`auth-form-${mode}`}>
            {mode === 'signup' && <>
              <Field icon={User} placeholder="Your name" value={form.owner} onChange={set('owner')} testId="auth-owner" />
              <Field icon={Building2} placeholder="Business name" value={form.name} onChange={set('name')} testId="auth-business" />
              <Field icon={Globe} placeholder="Website (optional)" value={form.website} onChange={set('website')} testId="auth-website" required={false} />
            </>}
            <Field icon={Mail} type="email" placeholder="Email" value={form.email} onChange={set('email')} testId="auth-email" />
            {mode !== 'forgot' && <Field icon={Lock} type="password" placeholder={mode === 'signup' ? 'Password (8+ characters)' : 'Password'} value={form.password} onChange={set('password')} testId="auth-password" />}
            <button type="submit" disabled={busy} data-testid="auth-submit" className="w-full btn-gold rounded-full py-3.5 flex items-center justify-center gap-2 disabled:opacity-70">
              {busy ? <Loader2 size={17} className="animate-spin" /> : <>{COPY[mode][1]} <ArrowRight size={17} /></>}
            </button>
          </form>
          <div className="mt-6 flex items-center justify-between text-sm">
            <button onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')} data-testid="auth-toggle-mode" className="text-cream/60 hover:text-cream transition-colors">
              {mode === 'signin' ? "New here? Create account" : 'Have an account? Sign in'}
            </button>
            {mode === 'signin' && <button type="button" onClick={() => setMode('forgot')} data-testid="auth-forgot" className="text-gold/80 hover:text-gold transition-colors">Reset password</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, type = 'text', placeholder, value, onChange, testId, required = true }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border hairline px-4 py-3 focus-within:border-gold/50 transition-colors">
      <Icon size={17} className="text-gold/70 shrink-0" />
      <input type={type} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testId}
        className="bg-transparent flex-1 outline-none text-cream placeholder:text-cream/35" />
    </div>
  );
}
